import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, TriangleAlert } from 'lucide-react';
import type { MessagingConnection } from '@tailoring-crm/shared';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  useConfirm,
  Input,
  Label,
} from '@tailoring-crm/ui';
import { formatDateTime } from '../../lib/format.js';

/**
 * Connecting the shop's own WhatsApp workspace.
 *
 * The card is built around one fact the owner has to be able to trust: the key they paste is
 * never shown again, by anyone, including us. So the field is empty on every visit, the saved
 * key is described by its last four characters, and saving verifies immediately — a mistyped
 * key is caught while the person who typed it is still looking at the screen, and the number
 * customers will see comes back from the provider rather than being typed a second time.
 *
 * When verification fails, the provider's own words are shown. "Could not connect" would teach
 * a shop to ignore this card; "the API key was rejected by the workspace" tells them what to do.
 *
 * `readOnly` is for the people the API will answer but never obey — a manager holds
 * `settings.read` and not `settings.notifications.write`, so every write they could start would
 * come back a 403. The controls are then absent rather than disabled: a greyed-out button still
 * says "you could do this", and they could not. Every FACT stays, because a manager asked
 * "is WhatsApp working?" needs the same answer an owner gets.
 */

interface ConnectionActions {
  saving: boolean;
  testing: boolean;
  onSave: (apiKey: string) => Promise<void>;
  onTest: () => Promise<void>;
  onDisconnect: () => Promise<void>;
}

export interface WritableConnectionCardProps extends ConnectionActions {
  connection: MessagingConnection;
  readOnly?: false;
}

/** Read-only needs no handlers, but tolerates a caller that has them to hand anyway. */
export interface ReadOnlyConnectionCardProps extends Partial<ConnectionActions> {
  connection: MessagingConnection;
  readOnly: true;
  /**
   * What to say when there is no workspace yet, if the default sentence would be untrue.
   *
   * The default names an owner as the person who can fix this, which is right when the reader
   * is a manager and wrong when the whole shop is locked — under a plan lock or an unpaid
   * subscription there is no owner who can connect it either. The caller knows which refusal
   * it is applying, so the caller supplies the sentence.
   */
  emptyMessage?: string;
}

export type ConnectionCardProps = WritableConnectionCardProps | ReadOnlyConnectionCardProps;

export function ConnectionCard(props: ConnectionCardProps): ReactElement {
  const { t } = useTranslation();
  const { connection } = props;
  const readOnly = props.readOnly === true;

  const connected = connection.status !== 'not_connected';

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle>{t('notifications.connectionTitle')}</CardTitle>
          <ConnectionStatusBadge connection={connection} />
        </div>
        <CardDescription>{t('notifications.connectionDescription')}</CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col gap-4">
        {connection.lastError ? (
          <p
            role="alert"
            className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm font-medium text-destructive"
          >
            <TriangleAlert aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
            {connection.lastError}
          </p>
        ) : null}

        {!connection.live ? (
          <p className="rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-sm text-warning">
            {t('notifications.fakeProviderWarning')}
          </p>
        ) : null}

        {connected ? (
          <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
            <div>
              <dt className="text-muted-foreground">{t('notifications.sendingNumber')}</dt>
              <dd className="font-medium">{connection.sendingNumber ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">{t('notifications.workspace')}</dt>
              <dd className="font-medium">
                {connection.workspaceName ?? connection.workspaceId ?? '—'}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">{t('notifications.savedKey')}</dt>
              {/* The only thing the product will ever show of the key. */}
              <dd className="font-medium">
                {connection.apiKeyLast4 ? `•••• ${connection.apiKeyLast4}` : '—'}
              </dd>
            </div>
            {/*
              Only on the read-only card. Somebody who can press "Test connection" can find out
              whether it works right now; somebody who cannot has nothing but the date it last
              did, so that date has to be on the screen. Adding it to the writable card would
              change a screen this change is not allowed to change.
            */}
            {readOnly && connection.verifiedAt ? (
              <div>
                <dt className="text-muted-foreground">{t('notifications.verifiedAt')}</dt>
                <dd className="font-medium">{formatDateTime(connection.verifiedAt)}</dd>
              </div>
            ) : null}
          </dl>
        ) : null}

        {props.readOnly === true ? (
          connected ? null : (
            // Without this the card is a title, a grey badge and nothing else — a box that
            // looks broken rather than one that is simply somebody else's job.
            <p className="text-sm text-muted-foreground">
              {props.emptyMessage ?? t('notifications.connectionReadOnlyEmpty')}
            </p>
          )
        ) : (
          <ConnectionControls
            connected={connected}
            saving={props.saving}
            testing={props.testing}
            onSave={props.onSave}
            onTest={props.onTest}
            onDisconnect={props.onDisconnect}
          />
        )}
      </CardContent>
    </Card>
  );
}

function ConnectionControls({
  connected,
  saving,
  testing,
  onSave,
  onTest,
  onDisconnect,
}: ConnectionActions & { connected: boolean }): ReactElement {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [apiKey, setApiKey] = useState('');

  const submit = async (): Promise<void> => {
    if (apiKey.trim().length === 0) return;
    await onSave(apiKey.trim());
    // Held only for the length of the request. Nothing in the app keeps the shop's key.
    setApiKey('');
  };

  return (
    <>
      <form
        className="flex flex-col gap-3 sm:flex-row sm:items-end"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
        noValidate
      >
        <div className="flex flex-1 flex-col gap-1.5">
          <Label htmlFor="relay-api-key">
            {connected ? t('notifications.replaceKey') : t('notifications.apiKey')}
          </Label>
          <Input
            id="relay-api-key"
            type="password"
            autoComplete="off"
            value={apiKey}
            onChange={(event) => setApiKey(event.target.value)}
            placeholder={t('notifications.apiKeyPlaceholder')}
          />
        </div>
        <Button type="submit" disabled={saving || apiKey.trim().length === 0}>
          {t('notifications.saveAndVerify')}
        </Button>
      </form>

      {connected ? (
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" disabled={testing} onClick={() => void onTest()}>
            {t('notifications.testConnection')}
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              void (async () => {
                const confirmed = await confirm({
                  title: t('notifications.disconnectTitle'),
                  description: t('notifications.disconnectDescription'),
                  confirmLabel: t('notifications.disconnect'),
                  destructive: true,
                });
                if (confirmed) await onDisconnect();
              })();
            }}
          >
            {t('notifications.disconnect')}
          </Button>
        </div>
      ) : null}
    </>
  );
}

/**
 * Connected, broken, or never set up — the one sentence everything else on both screens hangs
 * off. Exported because the notifications screen says the same thing in a single line without
 * the card around it, and two independently written badges would eventually disagree about
 * what "connected" looks like.
 */
export function ConnectionStatusBadge({
  connection,
}: {
  connection: MessagingConnection;
}): ReactElement {
  const { t } = useTranslation();
  if (connection.status === 'connected') {
    return (
      <Badge variant="success" className="gap-1">
        <CheckCircle2 aria-hidden="true" className="size-3.5" />
        {t('notifications.statusConnected')}
      </Badge>
    );
  }
  if (connection.status === 'error') {
    return <Badge variant="destructive">{t('notifications.statusError')}</Badge>;
  }
  return <Badge variant="outline">{t('notifications.statusNotConnected')}</Badge>;
}
